import type { ReactNode } from 'react'
import Card from './Card'

interface Column<T> {
  /** Column header — used as <th> on desktop, dl label on mobile. */
  header: string
  /** Key into the row object. */
  field: keyof T & string
  /** Custom cell renderer. Falls back to String(value). */
  render?: (value: T[keyof T], row: T) => ReactNode
  /** Optional override for the mobile label (defaults to `header`). */
  mobileLabel?: string
  /** Skip this column in the mobile card. Useful for ID / order columns. */
  hideOnMobile?: boolean
}

interface MobileTableCardsProps<T> {
  data: T[]
  columns: Column<T>[]
  rowKey: keyof T & string
  /** Optional empty-state message when `data` is empty. */
  emptyMessage?: string
}

/**
 * MobileTableCards — render the same row data as either stacked cards
 * (< sm) or a real <table> (≥ sm), controlled entirely by CSS. No
 * `useMediaQuery`, no JS, no hydration flash.
 *
 * Replaces the previous pattern of `overflow-x-auto` + `min-w-[36rem]`
 * tables that forced every phone to swipe horizontally. The
 * 3 call sites in scope (Products, ValuesIncubator, Harbour) all
 * had this exact antipattern.
 */
export default function MobileTableCards<T extends object>({
  data,
  columns,
  rowKey,
  emptyMessage = '暫無資料',
}: MobileTableCardsProps<T>) {
  if (data.length === 0) {
    return (
      <p className="text-sm text-ink-soft text-center py-8">{emptyMessage}</p>
    )
  }

  const visibleMobileColumns = columns.filter((c) => !c.hideOnMobile)

  return (
    <>
      {/* Mobile: stacked cards, one per row */}
      <ul className="sm:hidden flex flex-col gap-gap-cards list-none p-0 m-0">
        {data.map((row) => (
          <li key={String(row[rowKey])}>
            <Card density="compact" className="mtc-card">
              <dl className="space-y-2.5">
                {visibleMobileColumns.map((col) => {
                  const value = row[col.field]
                  return (
                    /* Label above, value below, both centred. The pair used
                       to sit on one line with `justify-between`, which
                       pushed the value flush to the right edge of the
                       card and read as a right-aligned column on a
                       narrow screen. */
                    <div key={col.field} className="mtc-row">
                      <dt className="mtc-row__label text-xs font-bold text-ink">
                        {col.mobileLabel ?? col.header}
                      </dt>
                      <dd className="mtc-row__value text-sm text-ink min-w-0">
                        {col.render
                          ? col.render(value as T[keyof T], row)
                          : String(value ?? '—')}
                      </dd>
                    </div>
                  )
                })}
              </dl>
            </Card>
          </li>
        ))}
      </ul>

      {/* sm+: real table, kept scrollable for very wide datasets */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-border-strong">
              {columns.map((col) => (
                <th
                  key={col.field}
                  scope="col"
                  className="text-left text-sm font-semibold text-ink-soft py-3 px-4 whitespace-nowrap"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr
                key={String(row[rowKey])}
                className="border-b border-border last:border-b-0 align-top"
              >
                {columns.map((col) => {
                  const value = row[col.field]
                  return (
                    <td
                      key={col.field}
                      className="py-3 px-4 text-sm text-ink"
                    >
                      {col.render
                        ? col.render(value as T[keyof T], row)
                        : String(value ?? '—')}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
